import type { GridSortModel, DataGridProps as MuiDataGridProps, GridColDef as MuiGridColDef, GridRenderCellParams as MuiGridRenderCellParams } from '@ethings/ui-toolkit';
import { DataGrid, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import type { GridValidRowModel } from '@mui/x-data-grid';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluate } from '../../evaluator';

type GridColDef = MuiGridColDef & {
  template?: string;
}

interface Sort {
  field: string;
  direction: 'asc' | 'desc' | undefined;
  type: string;
}

interface DataGridProps extends MuiDataGridProps {
  onSortChange?: (sort: Sort) => void;
  sort?: Sort;
}

const renderCellTemplate = (params: MuiGridRenderCellParams<GridValidRowModel, unknown, unknown>): ReactNode => {
  const colDef = params.colDef as GridColDef | undefined;
  const row = params.row;

  if (colDef?.template && typeof row === 'object' && row !== null) {
    return <>{evaluate(colDef.template, row)}</>;
  }

  return null;
};

const NoResultsOverlay: FC = () => {
  const { t } = useTranslation('common');
  return <Box p={2}><Typography variant='body2'>{t('common.hint.list.no-results')}</Typography></Box>;
};

const hasTemplate = (col: MuiGridColDef): col is GridColDef & { template: string } => {
  const template = (col as GridColDef).template;
  return typeof template === 'string' && template.length > 0;
};

export const DynamicDataGrid: FC<DataGridProps> = ({ onSortChange, sort, ...props }) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns = (props.columns ?? []) as MuiGridColDef[];

    return baseColumns.map((col) => {
      if (hasTemplate(col)) {
        return {
          ...(col as GridColDef),
          renderCell: renderCellTemplate,
        };
      }

      return col as GridColDef;
    });
  }, [props.columns]);

  useEffect(() => {
    if (!sort || !sort.direction) {
      setSortModel([]);
      return;
    }

    setSortModel([{ field: sort.field, sort: sort.direction }]);
  }, [sort]);

  const handleSortChange = (newSortModel: GridSortModel): void => {
    const firstSort = newSortModel[0];

    if (!firstSort) {
      setSortModel([]);
      onSortChange?.({ field: '', direction: undefined, type: '' });
      return;
    }

    const field = firstSort.field;
    const direction = firstSort.sort ?? 'asc';
    const col = columns.find((c) => c.field === field);

    if (!col) {
      return;
    }

    setSortModel(newSortModel);
    onSortChange?.({ field, direction, type: col.type ?? '' });
  };

  return <DataGrid
    {...props}
    onSortModelChange={handleSortChange}
    sortModel={sortModel}
    columns={columns}
    slots={{
      noResultsOverlay: NoResultsOverlay,
      noRowsOverlay: NoResultsOverlay,
    }}
  />;
};
